import { FC, useCallback, useEffect, useState } from "react";
import ".././Jobs.scss";
import { useAppDispatch, useAppSelector } from "../../../../store";
import CandidateJobHeader from "../CandidateJobHeader";
import CandidateVacancyCard from "../CandidateVacancyCard";
import {
  DEFAULT_ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_OPTIONS,
} from "../../../../constants/app";
import PaginationControls from "../../../../utils/PaginationControls";
import { fetchAcceptedListings } from "../../../../services/candidateJobsService";
import ActivationAlert from "../Elements/ActivationAlert";
import SortControls from "../Elements/SortControls";
import SortFilterDropdown, { SortOrder } from "../../../UI/SortFilterDropdown";
import { timesheetFilterOptions } from "../../../../models/vacancy";
import { TTimesheetSort } from "../../../../store/timesheet/types";
import ExpiringNotifications from "../Elements/ExpiringNotification";

/**
 * The `AcceptedListing` component is responsible for displaying a list of job listings
 * that have been accepted by a candidate. It includes features such as pagination, sorting,
 * and the ability to customize the number of items displayed per page.
 *
 * ## Features:
 * - Fetches and displays a list of accepted job listings for a candidate.
 * - Supports pagination with dynamic page navigation.
 * - Allows sorting of job listings by various fields (e.g., creation date).
 * - Provides controls for changing the number of items displayed per page.
 * - Displays a header with job listing statistics and sorting controls.
 *
 * ## State Management:
 * - Utilizes `useAppDispatch` and `useAppSelector` for Redux state management.
 * - Tracks pagination, sorting, and items per page using React's `useState`.
 *
 * ## Dependencies:
 * - `dispatch`: Redux dispatch function for triggering actions.
 * - `candidateJobsList`: Redux state containing the list of accepted jobs.
 * - `fetchAcceptedListings`: Function to fetch the accepted job listings.
 *
 * ## Props:
 * This component does not accept any props.
 *
 * ## Hooks:
 * - `useCallback`: Memoizes the `getCandidateJobList` function to prevent unnecessary re-creations.
 * - `useEffect`: Triggers data fetching and updates pagination state when dependencies change.
 *
 * ## Methods:
 * - `getCandidateJobList`: Fetches the list of accepted job listings.
 * - `handlePageChange`: Updates the current page based on pagination link clicks.
 * - `handlePageSizeChange`: Updates the number of items displayed per page.
 * - `handleTimeFrameSort`: Toggles sorting order and updates the sorting field.
 *
 * ## Rendered Components:
 * - `ActivationAlert`: Displays an alert for activation-related information.
 * - `CandidateJobHeader`: Displays job listing statistics.
 * - `SortControls`: Provides sorting controls for the job listings.
 * - `CandidateVacancyCard`: Renders individual job listing cards.
 * - `PaginationControls`: Provides pagination controls for navigating through job listings.
 *
 * ## Example Usage:
 * ```tsx
 * <AcceptedListing />
 * ```
 *
 * ## Notes:
 * - Ensure that the Redux store is properly configured with the `candidateJobs` slice.
 * - The component assumes the presence of constants like `DEFAULT_ITEMS_PER_PAGE` and `ITEMS_PER_PAGE_OPTIONS`.
 */
const AcceptedListing: FC = () => {
  const dispatch = useAppDispatch();

  const verificationStatus = useAppSelector(
    (state) => state.onboarding.personaldetails?.verification_status
  );
  const documentsExpired = useAppSelector(
    (state) => state.onboarding.userpersonaldetails?.is_documents_expired
  );
  const candidateJobsList = useAppSelector(
    (state) => state.candidateJobs.candidateAcceptedJobsList
  );
  const candidateJobsListData = candidateJobsList?.accepted_jobs;
  
  const [itemPerPageList, setItemPerPageList] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationLinks, setPaginationLinks] = useState<any[]>([]);
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<string>("desc");

  /**
   * Fetches the list of accepted job listings for a candidate.
   *
   * This function is memoized using `useCallback` to prevent unnecessary re-creations
   * and ensures that the fetch operation is performed with the latest parameters.
   *
   * Dependencies:
   * - `dispatch`: The dispatch function to trigger the fetch action.
   * - `currentPage`: The current page number for pagination.
   * - `itemPerPageList`: The number of items per page for pagination.
   * - `sortOrder`: The order in which the results should be sorted (e.g., ascending or descending).
   * - `sortBy`: The field by which the results should be sorted.
   *
   * @async
   * @function
   * @returns {Promise<void>} A promise that resolves when the fetch operation is complete.
   */
  const getCandidateJobList = useCallback(async () => {
    await fetchAcceptedListings(dispatch, {
      page: currentPage,
      per_page: itemPerPageList,
      sort_order: sortOrder,
      sort_by: sortBy,
    });
  }, [dispatch, currentPage, itemPerPageList, sortBy, sortOrder]);

  useEffect(() => {
    getCandidateJobList();
  }, [currentPage, itemPerPageList, getCandidateJobList]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (candidateJobsListData) {
      setTotalPages(candidateJobsListData?.last_page || 1);
      setPaginationLinks(candidateJobsListData?.links);
    }
  }, [candidateJobsListData]);

  /**
   * Handles the change in the number of items displayed per page.
   * Updates the items per page state and resets the current page to the first page.
   *
   * @param value - The new number of items per page, provided as a string or number.
   */
  const handlePageSizeChange = (value: string | number) => {
    setItemPerPageList(Number(value));
    setCurrentPage(1); // Reset to the first page when changing items per page
  };

  /**
   * Handles the sorting of the job listings based on the selected field and order.
   * @param type - The type of sorting to be applied (e.g., "created_at").
   *
   */
  const handleTimeFrameSort = (type: string) => {
    setSortBy(type);
    setSortOrder(sortOrder === "asc" ? "desc" : "asc");
  };

  const handleLocalSortChange = (field: string, order: SortOrder) => {
    timesheetFilterOptions.filter(option => {
      if (option.label === field) {
          handleTimeFrameSort(option.value as TTimesheetSort);
        return true;
      }
      return false;
    });
  };

  return (
    <>
      <div className="d-flex flex-column gap-20 gap-mobile-1">
        <ActivationAlert />
        { verificationStatus && verificationStatus === "verified" ? <ExpiringNotifications /> : "" }
        <CandidateJobHeader
          jobListingCount={candidateJobsList?.total_job_listing}
          pendingJobCount={candidateJobsList?.total_pending_jobs}
          acceptedJobCount={candidateJobsList?.total_accepted_jobs}
          isCandidateFilledAllForms={candidateJobsList?.is_user_step_exists}
          documentsExpired={documentsExpired}
        />
        <div className="d-flex flex-column">
          {/** Mobile Sort */}
          <SortFilterDropdown
            sortFields={["Position Type", "Requested Timeframe", "Posted", "Accepted"]}
            defaultField="Posted"
            onChange={handleLocalSortChange}
            defaultSortOrder={sortOrder as SortOrder}
          />
          {/** Lissting header */}
          <SortControls
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleTimeFrameSort}
            page_type="accepted"
          />
          {/** Listing data */}
          {candidateJobsListData && candidateJobsListData?.data?.length > 0 ? (
            candidateJobsListData?.data?.map((vacancy, index) => (
              <CandidateVacancyCard
                key={index}
                vacancy={vacancy}
                type="accepted"
                isCandidateFilledAllForms={
                  candidateJobsList?.is_user_step_exists
                }
                getCandidateJobList={getCandidateJobList}
                documentsExpired={documentsExpired}
              />
            ))
          ) : (
            <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
              <div className="fs-16 fw-400 text-grey-medium">No Jobs Found</div>
            </div>
          )}
        </div>
        {/* Pagination Component */}
        {candidateJobsList &&
          candidateJobsList?.total_pending_jobs > DEFAULT_ITEMS_PER_PAGE && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              paginationLinks={paginationLinks}
              itemsPerPage={itemPerPageList}
              itemsPerPageOptions={ITEMS_PER_PAGE_OPTIONS}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handlePageSizeChange}
            />
          )}
        {/* Pagination Component */}
      </div>
    </>
  );
};

export default AcceptedListing;
